import * as vp from 'vopay';

import { FunctionComponent, useEffect, useState, useCallback } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import axios from 'axios';
import vopayApi from '../../../services/api.config';
import Account from './SubAccount';
import { useModuleFeatureFlag } from '../../../providers/FeatureFlag.provider';
import type { AccountsHandlers, Balances as Balance } from '../types';

type SubAccountContainerProps = {
  api: vp.Api;
  handlers: AccountsHandlers;
};

type FC = FunctionComponent<SubAccountContainerProps>;

const SubAccountContainer: FC = ({ api, handlers }: SubAccountContainerProps) => {
  const history = useHistory();
  const { canAccessOnboardingTokenPanel } = useModuleFeatureFlag('accounts');
  const query = new URLSearchParams(useLocation().search);
  const balance: Balance = {
    account: query.get('account') || '0',
    pending: query.get('pending') || '0',
    available: query.get('available') || '0',
    immediate: query.get('immediate') || '0',
    reserve: query.get('reserve') || '0',
    currency: (query.get('currency') as vp.SupportedCurrencies) || 'CAD',
    isActive: query.get('isActive') === 'true',
    accountStatus: query.get('accountStatus') || '',
    security: query.get('security') || '',
  };

  const { accountId } = useParams<{ accountId: string }>();
  const [hasValidOnboardingUrl, setHasValidOnboardingUrl] = useState<boolean | null>(null);
  const [onBoardingLink, setOnboardingLink] = useState<string | null>(null);
  const [fetchOnboardingUrlError, setFetchOnboardingUrlError] = useState<string>('');
  const source = axios.CancelToken.source();

  const handleAccountOnboardingClick = () => {
    if (onBoardingLink) window.open(onBoardingLink);
  };

  const handleCopyClick = () => {
    if (onBoardingLink) navigator?.clipboard?.writeText(onBoardingLink);
  };

  const fetchOnboardingUrl = async () => {
    if (!canAccessOnboardingTokenPanel) return;
    if (balance.isActive) return;

    setFetchOnboardingUrlError('');
    setHasValidOnboardingUrl(null);
    source.cancel();
    const partnerOnboardingUrlInfo = await api.fetchOnboardingAppUrl({ VopayAccountID: accountId }, source);
    const { ErrorMessage, OnboardingURL } = partnerOnboardingUrlInfo;

    if (OnboardingURL) {
      setOnboardingLink(OnboardingURL);
      setHasValidOnboardingUrl(true);
    } else if (ErrorMessage) {
      setHasValidOnboardingUrl(false);
      setFetchOnboardingUrlError(ErrorMessage);
    }
  };

  const getAccount = useCallback(() => {
    const [accounts, setAccounts] = useState(
      <Account
        hasValidOnboardingUrl={hasValidOnboardingUrl}
        handlers={handlers}
        onActionClick={hasValidOnboardingUrl ? handleAccountOnboardingClick : fetchOnboardingUrl}
        onCopyClick={handleCopyClick}
        onBoardingLink={onBoardingLink}
      />,
    );

    useEffect(() => {
      setAccounts(
        <Account
          hasValidOnboardingUrl={hasValidOnboardingUrl}
          handlers={handlers}
          onActionClick={hasValidOnboardingUrl ? handleAccountOnboardingClick : fetchOnboardingUrl}
          onCopyClick={handleCopyClick}
          onBoardingLink={onBoardingLink}
        />,
      );
    }, [hasValidOnboardingUrl, onBoardingLink]);

    return accounts;
  }, [hasValidOnboardingUrl, onBoardingLink]);

  return (
    <>
      {getAccount()}

      <vp.ConfirmationDialogue
        isVisible={!!fetchOnboardingUrlError}
        title="Error! Your onboarding url could not be retrieved."
        subTitle={fetchOnboardingUrlError}
        status="error"
        confirm={{
          label: 'Back to Accounts',
          action: () => {
            setFetchOnboardingUrlError('');
            history.push('/accounts');
          },
        }}
        cancel={{
          label: 'Stay Here',
          action: () => setFetchOnboardingUrlError(''),
        }}
      />
    </>
  );
};

export default vopayApi.withApiComponent<SubAccountContainerProps>(SubAccountContainer);
